import {SubscriptionManager, wrapError} from '@northpoint-development/labs-common-core';
import type {MVTLayerPickingInfo} from '@deck.gl/geo-layers';
import type {PickingInfo} from '@deck.gl/core';

import {Graphic} from '../Graphic';
import {MapLayer} from '../MapLayer';
import {EventService} from '@/services/EventService';
import {TokenService} from '@/services/TokenService';
import {MapService} from '@/services/MapService';
import {MVTLayers} from '@/services/MapService/MVTLayers';
import {createVectorTileContent} from '@/lib/classes/InfoWindowContent';
import {SubscriptionEvent, SubscriptionResult} from '@/lib/filters/subscriptions';
import {handleOsmLayerClick} from './customClickHandlers/osm-layers';
import {handleLightboxZoningBoundariesClick, handleLightboxParcelsClick} from './customClickHandlers/lightbox';
import {LegendGroup} from '@/lib/classes/Legend/LegendGroup';
import type {ActiveFiltersByLayer} from '@/lib/filters/layers/layerFilters';
import {normalize} from '@/lib/strings';
import {getStore} from '@/services/store';

type Event = {
  latLng: google.maps.LatLng;
  features: MVTLayerPickingInfo[];
};

const REDRAW_MS = 100;

const cachedToken = {
  token: undefined as string | undefined,
  expiration: undefined as number | undefined,
  inProgress: false as Promise<void> | false,
};

/**
 * A mapping of custom click handler IDs to their respective content functions. Currently, it is expected these
 * functions return the exact content the action `'open_info_window'` expects.
 */
const customClickHandlers = {
  'Power (OpenStreetMap)': handleOsmLayerClick,
  'Water (OpenStreetMap)': handleOsmLayerClick,
  'Oil & Gas (OpenStreetMap)': handleOsmLayerClick,
  'Lightbox Zoning Boundaries': handleLightboxZoningBoundariesClick,
  'Lightbox Parcels': handleLightboxParcelsClick,
};

// BMAPS-2783: When we are using sublayer `idField` for all feature id keys,
// we shouldn't need this function at all anymore.
const getFeatureIdValue = (feature, featureIdKey: string): number => {
  if (feature[featureIdKey]) {
    return feature[featureIdKey];
  }
  for (const key in feature) {
    if (normalize(key) === normalize(featureIdKey)) {
      return feature[key];
    }
  }
  if (feature.id) {
    return feature.id;
  }
  return undefined;
};

/** Graphic representing ESRI Vector Tile information on the map. */
class VectorTileGraphic extends Graphic {
  private clickable: boolean;
  private visible: boolean;
  private name: string;

  legendGroups: LegendGroup[];
  visibleLayers: string[];
  activeFilters: ActiveFiltersByLayer;
  featureIdKey: string;
  subscriptions: SubscriptionManager;
  fillOpacity: number | undefined;
  selectedFeatureId: number | null;
  private redrawTimer: ReturnType<typeof setTimeout> | undefined;


  constructor(layer: MapLayer) {
    super();

    const subLayers = layer.displaySubLayers?.length ? layer.displaySubLayers : layer.activeSubLayers();

    this.clickable = false;
    this.visible = false;
    this.name = layer.name;
    this.legendGroups = layer.legendGroups;
    this.visibleLayers = subLayers.reduce((acc, subLayer) => acc.concat(subLayer.visibleLayers), []).toReversed();
    this.activeFilters = layer.activeFilters;
    this.featureIdKey = subLayers[0].featureIdKey ?? subLayers[0]?.idFields?.[subLayers[0]?.id] ?? subLayers[0].idField;
    this.subscriptions = new SubscriptionManager();
    this.fillOpacity = layer.fillOpacityEnabled ? layer.fillOpacity : undefined;
    this.selectedFeatureId = null;

    MVTLayers.create({
      data: layer.urlMap['tileServer'],
      iconUrls: {
        iconAtlas: layer.urlMap['iconAtlas'],
        iconMapping: layer.urlMap['iconMapping'],
      },
      featureIdKey: this.featureIdKey,
      id: layer.name,
      isPublicLayer: layer.isPublicLayer,
      minZoom: layer.minZoom,
      maxZoom: layer.maxZoom,
      styleJson: layer.styleSheet,
    });

    if (!subLayers.some((layer) => layer.clickableLayers.length)) return;

    this.setClickable(true);
    this.addHandler('click', `vector-${layer.id}-click`, async (event: Event) => {
      if (layer.customClickHandler) {
        const contents = await customClickHandlers[layer.customClickHandler](event, layer, this);
        if (!contents?.length) {
          console.warn(`No content returned from custom click handler: ${layer.customClickHandler}`);
          return;
        }
        this.subscriptions.publish('open_info_window', contents);
      } else {
        if (layer.showLoadingCursor) {
          MapService.setCursor('wait');
        }
        try {
          const subjects = await this.handleFeatureClick(event.latLng, event.features, layer);
          if (!subjects?.length) return;

          const currentSubLayers = layer.activeSubLayers();
          const contents = subjects.map((
            subject: {layerName: string; feature: Record<string, unknown>; fields: unknown[]},
          ) => {
            const sublayer = currentSubLayers.find((sl) => sl.id === subject.layerName) ?? currentSubLayers[0];
            return {
              content: createVectorTileContent(
                {
                  banFields: layer.options[0].banFields ?? [],
                  banValues: layer.options[0].banValues ?? [],
                  idField: (sublayer?.idFields?.[subject.layerName] ?? sublayer?.idField ?? '').toLowerCase(),
                  name: layer.name,
                  sublayerName: subject.layerName,
                  titleFields: sublayer?.titleFields ?? [],
                  fields: subject.fields,
                  customFieldMapping: layer.options[0].customFieldMapping,
                  schemaVersion: layer.schemaVersion,
                  tableConfig: sublayer?.tableConfig,
                },
                {
                  layerName: subject.layerName,
                  feature: subject.feature,
                },
                this,
              ),
            };
          });
          if (layer.showLoadingCursor) {
            MapService.setCursor('default');
          }
          this.subscriptions.publish('open_info_window', contents);
        } finally {
          if (layer.showLoadingCursor) {
            MapService.setCursor('default');
          }
        }
      }
    });
  }

  /** Redraws the tile layer from this graphic's current state. */
  private redraw(visible: boolean = this.visibleLayers.length > 0) {
    MVTLayers.drawTileLayer(this.name, {
      visible,
      visibleLayers: this.visibleLayers,
      legendGroups: this.legendGroups,
      fillOpacity: this.fillOpacity,
      activeFilters: this.activeFilters,
      selectedFeatureId: this.selectedFeatureId,
    });
  }

  /** Toggles the visibility of the MVTLayer. */
  setMap(map: google.maps.Map | null) {
    const visible = (map !== null);
    if (this.visible === visible) return;
    this.visible = visible;
    if (!visible) {
      this.selectedFeatureId = null;
    }
    this.redraw(visible);
  }

  /**
   * Adds an event handler to this graphic.
   * @param event The event name this handler should cover.
   * @param handlerName A name identifying this handler.
   * @param handler A function to handle the events.
   */
  addHandler(event: string, handlerName: string, handler) {
    // Create a map click handler that, when clicked, triggers a click on the mvtSource
    const vectorTileClickHandler = (mapEvent: google.maps.MapMouseEvent) => {
      const features = MVTLayers.getLayerFeaturesAtPosition(this.name, {
        x: (mapEvent.domEvent as MouseEvent).pageX,
        y: (mapEvent.domEvent as MouseEvent).pageY,
      });
      if (features?.length) {
        handler({
          latLng: mapEvent.latLng,
          features,
        });
      }
    };

    // Wrap the handler in logic to suppress the handler if this graphic is not clickable.
    let wrapped = vectorTileClickHandler;
    if (event === 'click') {
      wrapped = (...args) => {
        if (!this.clickable || !this.visible) return;
        return vectorTileClickHandler(...args);
      };
    }

    EventService.addLayerHandler(this.name, handlerName, wrapped);
  }

  /**
   * Cached FeatureServer metadata keyed by URL. Only the layer name-to-ID mapping is consumed.
   * No invalidation needed — layer definitions are static, and the cache is scoped to this
   * instance which is created once per MapLayer per session.
   */
  private serverMetadataCache: Record<string, {layers: {id: number; name: string}[]}> = {};

  private async getServerMetadata(featureServerUrl: string, headers: Record<string, string>) {
    if (this.serverMetadataCache[featureServerUrl]) {
      return this.serverMetadataCache[featureServerUrl];
    }
    const formData = new URLSearchParams({f: 'json'});
    const serverResponse = await fetch(`${featureServerUrl}?${formData.toString()}`, {method: 'GET', headers});
    if (!serverResponse.ok) {
      throw new Error(`Error fetching feature server data: ${serverResponse.statusText}`);
    }
    const serverJson = await serverResponse.json();
    if (!serverJson?.layers?.length) {
      throw new Error('Feature server returned unexpected data.');
    }
    this.serverMetadataCache[featureServerUrl] = serverJson;
    return serverJson;
  }

  async handleFeatureClick(
    latLng: google.maps.LatLng,
    clickResults: PickingInfo[],
    layer: MapLayer,
  ) {
    // TODO: clean this up and move the network requests to a service or something
    if (!clickResults?.length) return;
    const featureServerUrl = layer.urlMap['featureServer'];
    if (!featureServerUrl) {
      console.warn('Missing feature server URL for vector layer.');
      return;
    }
    const headers: Record<string, string> = {'Content-Type': 'application/x-www-form-urlencoded'};
    if (!layer.isPublicLayer) {
      // TODO: this should be getting the token name from the layer config
      const token = await TokenService.getToken('arcGisServerToken');
      headers['Authorization'] = `Bearer ${token}`;
    }
    let serverJson;
    try {
      serverJson = await this.getServerMetadata(featureServerUrl, headers);
    } catch (error) {
      console.error(wrapError(error, 'Error fetching feature server data'));
      getStore().commit('setSnack', {message: 'Feature data is unavailable right now. Please try again.', color: 'error'});
      return;
    }

    const layerClickedFeatures: Record<string, number[]> = {};
    clickResults.forEach((feature) => {
      // @ts-ignore We know a Feature from Deck.gl will have an object property
      const featureProperties = feature?.object?.properties;
      const featureId = getFeatureIdValue(featureProperties, this.featureIdKey);
      if (layerClickedFeatures[`${featureProperties.layerName}`]) {
        layerClickedFeatures[`${featureProperties.layerName}`].push(featureId);
      } else {
        layerClickedFeatures[`${featureProperties.layerName}`] = [featureId];
      }
    });

    let layerDefs = {};
    for (const [layerName, featureIds] of Object.entries(layerClickedFeatures)) {
      const layerId = serverJson.layers.find((l) => l.name === layerName)?.id ?? serverJson.layers[0].id;
      // Certain ID fields are numeric, such as ObjectID, and ESRI doesn't like wrapping them in quotes
      const inString = isNaN(featureIds[0]) ? `'${featureIds.join(`','`)}'` : featureIds.join(',');
      layerDefs[`${layerId}`] = `${this.featureIdKey.toLowerCase()} IN (${inString})`;
    }

    const query = new URLSearchParams({f: 'json', layerDefs: JSON.stringify(layerDefs), returnGeometry: 'false'});
    let queryJson;
    try {
      const queryResponse = await fetch(`${featureServerUrl}/query?${query.toString()}`, {method: 'GET', headers});
      if (!queryResponse.ok) {
        console.error(`Error fetching feature data: ${queryResponse.statusText}`);
        getStore().commit('setSnack', {message: 'Feature data is unavailable right now. Please try again.', color: 'error'});
        return;
      }
      queryJson = await queryResponse.json();
    } catch (error) {
      console.error(wrapError(error, 'Error fetching feature data'));
      getStore().commit('setSnack', {message: 'Feature data is unavailable right now. Please try again.', color: 'error'});
      return;
    }
    if (!queryJson?.layers?.length) {
      console.error('Feature query returned unexpected data.');
      getStore().commit('setSnack', {message: 'Feature data is unavailable right now. Please try again.', color: 'error'});
      return;
    }

    return Object.keys(layerClickedFeatures).map((layerName) => {
      const layerId = serverJson.layers.find((l) => l.name === layerName)?.id ?? serverJson.layers[0].id;
      const layer = queryJson.layers.find((l) => l.id === layerId);
      const layerFields = layer.fields;
      return layer.features.map((feature) => ({
        layerName,
        feature: {
          ...feature.attributes,
          location: latLng,
        },
        fields: layerFields,
      }));
    }).flat();
  }

  /**
   * Updates the legend filtering for this graphic.
   * @param legendGroups An array of LegendGroup objects to update the legend groups for this graphic.
   */
  updateLegendFiltering(legendGroups: LegendGroup[], forceUpdate = false) {
    if (!forceUpdate && JSON.stringify(this.legendGroups) === JSON.stringify(legendGroups)) return;

    this.legendGroups = legendGroups;
    if (this.visible) {
      this.redraw();
    }
  }

  /**
   * Updates the attribute filters applied to this graphic's features.
   * @param activeFilters The active filters, keyed by the MVT layer name they apply to.
   */
  updateActiveFilters(activeFilters: ActiveFiltersByLayer) {
    if (JSON.stringify(this.activeFilters) === JSON.stringify(activeFilters)) return;

    this.activeFilters = activeFilters;
    if (this.visible) {
      this.redraw();
    }
  }

  // @ts-ignore We are overriding the parent method, but TypeScript doesn't appear to like this
  setHighlight(state: boolean, feature) {
    if (!this.visible) return;
    this.selectedFeatureId = state ? getFeatureIdValue(feature, this.featureIdKey) : null;
    this.redraw(true);
  }

  setClickable(clickable: boolean) {
    this.clickable = clickable;
  }

  tokenExpired() {
    return (!cachedToken.token || !cachedToken.expiration || cachedToken.expiration <= Date.now());
  }

  subscribe(eventName: string, callback: (event: SubscriptionEvent) => void): SubscriptionResult {
    return this.subscriptions.addSubscription(eventName, callback);
  }

  setVisibleLayers(layers: string[]) {
    // Prevent recalculation of deck.gl layer if the visible layers are the same
    if (JSON.stringify(this.visibleLayers) === JSON.stringify(layers)) return;
    this.visibleLayers = layers;
    if (this.visible) {
      this.redraw();
    }
  }

  /**
   * A redraw re-runs the paint accessors over every feature in every tile, so a slider drag gets at most one
   * per interval and the trailing redraw picks up whatever value the drag settled on.
   *
   * @param opacity a multiplier between 0 and 1, or undefined to go back to the stylesheet's own alpha
   */
  setFillOpacity(opacity: number | undefined) {
    if (this.fillOpacity === opacity) return;
    this.fillOpacity = opacity;
    if (!this.visible || this.redrawTimer) return;

    this.redrawTimer = setTimeout(() => {
      this.redrawTimer = undefined;
      this.redraw();
    }, REDRAW_MS);
  }
}

export {
  VectorTileGraphic,
  type Event,
};
